// Offline upload queue for photos and signatures (02 §2.2, "Photos and signatures offline").
//
// Firebase Storage does not queue uploads while offline and does not resume one after a restart.
// So a captured picture is first shrunk and saved in IndexedDB (never localStorage), and the
// caller gets the Storage path at once. The command that needs the picture names that path, and
// the queue uploads the bytes whenever there is signal: at app start, on reconnect, on a timer
// and on demand. A failed upload is retried with a growing pause and never given up on; a file
// stays until it is uploaded or a person discards it.
//
// Path: `uploads/{uid}/{ulid}.{ext}`. Storage rules (staff only, images only, 2 MB) check the
// path and the uploader, not the command, so it does not matter which arrives first.
import { createStore as createIdb, del, entries, get, keys, set } from 'idb-keyval';
import { getMetadata, ref, uploadBytes, type FirebaseStorage } from 'firebase/storage';
import { ulid } from 'ulid';
import {
  DEFAULT_MAX_SIDE,
  DEFAULT_QUALITY,
  ImageError,
  TWO_MB,
  compressImage,
  type CompressOptions,
  type CompressedImage,
} from '@ecobox/media';
import type { Connectivity } from './connectivity';
import { createStore, type ReadableStore } from './store';

export const UPLOAD_KINDS = ['signature', 'vehicle', 'pod', 'exception', 'bill'] as const;
export type UploadKind = (typeof UPLOAD_KINDS)[number];

export interface EnqueueOptions {
  kind: UploadKind;
}

/** What the queue knows about one picture that has not reached Storage yet. */
export interface UploadItem {
  id: string;
  /** The Storage path commands reference. */
  path: string;
  kind: UploadKind;
  uid: string;
  contentType: string;
  /** Bytes after compression. */
  size: number;
  /** ISO time it was captured. */
  createdAt: string;
  /** Failed attempts so far. */
  attempts: number;
  /** Epoch ms of the next attempt. */
  nextAttemptAt: number;
  lastError?: string;
}

export interface UploadSummary {
  /** Pictures on this phone not yet in Storage (waiting, uploading or failing). */
  pending: number;
  /** Being sent right now. */
  uploading: number;
  /** Tried at least once and failed: they will be tried again. */
  failing: number;
  /** Storage paths still waiting: a screen shows "photo pending upload" for these. */
  pendingPaths: readonly string[];
  items: readonly UploadItem[];
}

export const NO_UPLOADS: UploadSummary = {
  pending: 0,
  uploading: 0,
  failing: 0,
  pendingPaths: [],
  items: [],
};

/** Pause before attempt n+1 after n failures: 2 s, 5 s, 15 s, 30 s, 1 min, then every 5 min. */
export const RETRY_SCHEDULE_MS: readonly number[] = [2_000, 5_000, 15_000, 30_000, 60_000];
export const RETRY_CEILING_MS = 5 * 60_000;
const TICK_MS = 5_000;

export function retryDelayMs(failures: number): number {
  if (failures <= 0) return 0;
  return RETRY_SCHEDULE_MS[failures - 1] ?? RETRY_CEILING_MS;
}

/** Nobody is signed in, so there is no `uploads/{uid}` folder to write to. */
export class UploadNotSignedInError extends Error {
  constructor() {
    super('Sign in before taking pictures.');
    this.name = 'UploadNotSignedInError';
  }
}

/**
 * Puts the bytes at `path` with Firebase Storage. Storage rules let a picture be created once and
 * never changed (`allow update: if false`), so an upload that did reach Storage but whose answer
 * was lost on a bad connection is refused when it is tried again. That refusal is checked: if the
 * file is there with the same size, the upload is done.
 */
export async function uploadToStorage(
  storage: FirebaseStorage,
  path: string,
  bytes: ArrayBuffer,
  contentType: string,
): Promise<void> {
  const target = ref(storage, path);
  try {
    await uploadBytes(target, bytes, { contentType });
  } catch (err) {
    const code = typeof err === 'object' && err !== null && 'code' in err ? String(err.code) : '';
    if (code !== 'storage/unauthorized') throw err;
    let existing;
    try {
      existing = await getMetadata(target);
    } catch {
      // Not there (or not readable): the refusal was a real one.
      throw err;
    }
    if (existing.size !== bytes.byteLength) throw err;
  }
}

export interface UploadQueueDeps {
  /** The signed-in user's id, or null. Read each time, so a new sign-in is picked up. */
  getUid: () => string | null;
  connectivity: Pick<Connectivity, 'getSnapshot' | 'subscribe'>;
  /** Puts the bytes at `path`. Default: Firebase Storage `uploadBytes`. */
  upload?: (path: string, bytes: ArrayBuffer, contentType: string) => Promise<void>;
  /** Firebase Storage, for the default `upload`. Not needed when `upload` is given. */
  storage?: FirebaseStorage;
  /** Shrinks a picture. Default: `compressImage` from `@ecobox/media`. */
  compress?: (blob: Blob, kind: UploadKind) => Promise<CompressedImage>;
  /** IndexedDB database name prefix. Tests use their own so runs do not share files. */
  dbName?: string;
  now?: () => number;
  newId?: () => string;
  /** How often the queue looks for due uploads. Default 5 s. Set 0 in tests that drive `processNow`. */
  tickMs?: number;
}

export interface UploadQueue extends ReadableStore<UploadSummary> {
  /**
   * Shrinks the picture, saves it on this phone and returns the Storage path to put in the
   * command. It does not wait for the network: the upload happens in the background. Rejects
   * when the file is not a picture, cannot be made small enough, or nobody is signed in.
   */
  enqueueUpload(blob: Blob, options: EnqueueOptions): Promise<string>;
  /** Calls `listener` with the pending counts now and whenever they change. Returns a stop function. */
  watchUploads(listener: (summary: UploadSummary) => void): () => void;
  /** Whether a path is still waiting to reach Storage. */
  isPending(path: string): boolean;
  /** Tries every waiting upload now, ignoring the retry pause. Resolves when the pass ends. */
  retryNow(): Promise<void>;
  /** One pass over the uploads that are due. Runs by itself; exposed for tests. */
  processNow(): Promise<void>;
  /** A person gives up on a picture. The command that names it will show "photo missing". */
  discard(id: string): Promise<void>;
  /** Resolves when the pictures saved by earlier runs have been loaded. */
  readonly ready: Promise<void>;
  /** Starts the background work (online, tick). Returns a stop function. */
  start(): () => void;
  dispose(): void;
}

type StoredMeta = UploadItem;

function messageOf(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Options per kind: signatures stay PNG so thin lines stay crisp; photos become JPEG. */
export function compressOptionsFor(kind: UploadKind): CompressOptions {
  return kind === 'signature'
    ? { type: 'image/png', maxSide: DEFAULT_MAX_SIDE, maxBytes: TWO_MB }
    : { type: 'image/jpeg', maxSide: DEFAULT_MAX_SIDE, quality: DEFAULT_QUALITY, maxBytes: TWO_MB };
}

export function createUploadQueue(deps: UploadQueueDeps): UploadQueue {
  const {
    getUid,
    connectivity,
    dbName = 'ecobox-uploads',
    now = Date.now,
    newId = ulid,
    tickMs = TICK_MS,
  } = deps;
  const compress =
    deps.compress ??
    ((blob: Blob, kind: UploadKind) => compressImage(blob, compressOptionsFor(kind)));
  const upload =
    deps.upload ??
    (async (path: string, bytes: ArrayBuffer, contentType: string): Promise<void> => {
      if (!deps.storage) throw new Error('Storage is not available.');
      await uploadToStorage(deps.storage, path, bytes, contentType);
    });

  const metaDb = createIdb(`${dbName}-meta`, 'uploads');
  const bytesDb = createIdb(`${dbName}-bytes`, 'uploads');
  const items = new Map<string, StoredMeta>();
  const uploading = new Set<string>();
  const store = createStore<UploadSummary>(NO_UPLOADS);
  let pass: Promise<void> | undefined;
  let again = false;
  let timer: ReturnType<typeof setInterval> | undefined;
  const stops: (() => void)[] = [];

  const publish = (): void => {
    const list = [...items.values()].sort((a, b) =>
      a.createdAt === b.createdAt
        ? a.id.localeCompare(b.id)
        : a.createdAt.localeCompare(b.createdAt),
    );
    store.set({
      pending: list.length,
      uploading: list.filter((i) => uploading.has(i.id)).length,
      failing: list.filter((i) => i.attempts > 0).length,
      pendingPaths: list.map((i) => i.path),
      items: list,
    });
  };

  const ready = (async () => {
    try {
      const rows = await entries<string, StoredMeta>(metaDb);
      const known = new Set<string>();
      for (const [id, meta] of rows) {
        items.set(id, meta);
        known.add(id);
      }
      // Bytes with no record are left over from a crash between the two writes: drop them.
      for (const id of await keys<string>(bytesDb)) {
        if (!known.has(id)) await del(id, bytesDb);
      }
      if (items.size > 0) publish();
    } catch (err) {
      console.error('Pictures waiting to upload could not be loaded.', err);
    }
  })();

  const online = (): boolean => connectivity.getSnapshot().online;

  const uploadOne = async (meta: StoredMeta): Promise<void> => {
    uploading.add(meta.id);
    publish();
    try {
      const bytes = await get<ArrayBuffer>(meta.id, bytesDb);
      if (!bytes) {
        // The file is gone from this phone: there is nothing to retry. Say so.
        console.error(`Picture ${meta.path} is no longer on this phone.`);
        items.delete(meta.id);
        await del(meta.id, metaDb);
        return;
      }
      await upload(meta.path, bytes, meta.contentType);
      items.delete(meta.id);
      await del(meta.id, metaDb);
      await del(meta.id, bytesDb);
    } catch (err) {
      const attempts = meta.attempts + 1;
      const next: StoredMeta = {
        ...meta,
        attempts,
        nextAttemptAt: now() + retryDelayMs(attempts),
        lastError: messageOf(err),
      };
      // A picture discarded while it was uploading stays gone.
      if (!items.has(meta.id)) return;
      items.set(meta.id, next);
      try {
        await set(meta.id, next, metaDb);
      } catch (saveErr) {
        console.error('Could not record a failed upload.', saveErr);
      }
    } finally {
      uploading.delete(meta.id);
      publish();
    }
  };

  const runPass = async (force: boolean): Promise<void> => {
    await ready;
    const uid = getUid();
    if (!uid) return;
    const due = [...items.values()]
      .filter((i) => i.uid === uid && (force || i.nextAttemptAt <= now()))
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    for (const meta of due) {
      if (!online()) break;
      // The file may have been discarded while an earlier one was uploading.
      const current = items.get(meta.id);
      if (current) await uploadOne(current);
    }
  };

  const drive = (force: boolean): Promise<void> => {
    if (pass) {
      again = true;
      return pass;
    }
    pass = (async () => {
      try {
        await runPass(force);
        while (again) {
          again = false;
          await runPass(force);
        }
      } finally {
        pass = undefined;
      }
    })();
    return pass;
  };

  const kick = (): void => {
    drive(false).catch((err: unknown) => console.warn('Upload pass failed.', err));
  };

  return {
    ready,
    subscribe: store.subscribe,
    getSnapshot: store.getSnapshot,

    async enqueueUpload(blob, { kind }) {
      const uid = getUid();
      if (!uid) throw new UploadNotSignedInError();
      if (!blob.type.startsWith('image/')) {
        throw new ImageError('not_image', 'Only pictures can be sent.');
      }
      await ready;
      const shrunk = await compress(blob, kind);
      const id = newId();
      const path = `uploads/${uid}/${id}.${shrunk.ext}`;
      const bytes = await shrunk.blob.arrayBuffer();
      const meta: StoredMeta = {
        id,
        path,
        kind,
        uid,
        contentType: shrunk.type,
        size: bytes.byteLength,
        createdAt: new Date(now()).toISOString(),
        attempts: 0,
        nextAttemptAt: 0,
      };
      // Bytes first: a record always has its file.
      await set(id, bytes, bytesDb);
      await set(id, meta, metaDb);
      items.set(id, meta);
      publish();
      if (online()) kick();
      return path;
    },

    watchUploads(listener) {
      listener(store.getSnapshot());
      return store.subscribe(() => listener(store.getSnapshot()));
    },

    isPending: (path) => store.getSnapshot().pendingPaths.includes(path),

    retryNow: () => drive(true),
    processNow: () => drive(false),

    async discard(id) {
      if (!items.has(id)) return;
      items.delete(id);
      publish();
      await del(id, metaDb);
      await del(id, bytesDb);
    },

    start() {
      if (stops.length > 0) return () => undefined;
      stops.push(connectivity.subscribe(() => online() && kick()));
      if (tickMs > 0) {
        timer = setInterval(kick, tickMs);
        stops.push(() => {
          if (timer !== undefined) clearInterval(timer);
          timer = undefined;
        });
      }
      // App start: whatever an earlier run left behind goes now.
      if (online()) kick();
      return () => {
        for (const stop of stops.splice(0)) stop();
      };
    },

    dispose() {
      for (const stop of stops.splice(0)) stop();
    },
  };
}
